import type { PublicApiCatalogEntry } from '../shared/portalTypes';

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'] as const;

const OPERATION_METHODS = new Set(HTTP_METHODS.map((method) => method.toLowerCase()));

export interface OpenApiDocument {
  info?: {
    title?: string;
    description?: string;
    version?: string;
  };
  paths?: Record<string, OpenApiPathItem>;
  components?: {
    schemas?: Record<string, OpenApiSchema>;
  };
}

export interface OpenApiPathItem {
  parameters?: OpenApiParameter[];
  [method: string]: OpenApiOperation | OpenApiParameter[] | undefined;
}

export interface OpenApiOperation {
  tags?: string[];
  summary?: string;
  description?: string;
  operationId?: string;
  parameters?: OpenApiParameter[];
  requestBody?: OpenApiRequestBody;
  responses?: Record<string, unknown>;
}

export interface OpenApiParameter {
  name: string;
  in: 'query' | 'path' | 'header' | 'cookie';
  required?: boolean;
  description?: string;
  schema?: OpenApiSchema;
  example?: unknown;
  examples?: Record<string, { value?: unknown }>;
}

export interface OpenApiRequestBody {
  required?: boolean;
  description?: string;
  content?: Record<string, OpenApiMediaType>;
}

export interface OpenApiMediaType {
  schema?: OpenApiSchema;
  example?: unknown;
  examples?: Record<string, { value?: unknown }>;
}

export interface OpenApiSchema {
  $ref?: string;
  type?: string;
  format?: string;
  nullable?: boolean;
  default?: unknown;
  example?: unknown;
  enum?: unknown[];
  properties?: Record<string, OpenApiSchema>;
  required?: string[];
  items?: OpenApiSchema;
  allOf?: OpenApiSchema[];
  oneOf?: OpenApiSchema[];
  anyOf?: OpenApiSchema[];
  additionalProperties?: boolean | OpenApiSchema;
}

export interface ApiOperation {
  key: string;
  api: PublicApiCatalogEntry;
  apiId: string;
  apiTitle: string;
  method: string;
  path: string;
  summary: string;
  description: string;
  operationId?: string;
  tags: string[];
  parameters: OpenApiParameter[];
  requestBody?: OpenApiRequestBody;
  responses?: Record<string, unknown>;
  document: OpenApiDocument;
  searchText: string;
}

export interface RequestBodyOption {
  contentType: string;
  required: boolean;
  description: string;
  sampleText: string;
}

export async function loadApiOperations(catalogEntries: PublicApiCatalogEntry[]): Promise<ApiOperation[]> {
  const operationGroups = await Promise.all(catalogEntries.map(async (entry) => {
    const response = await fetch(entry.openApiUrl, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`${entry.title} OpenAPI returned ${response.status}.`);
    }

    const document = await response.json() as OpenApiDocument;
    return createOperations(entry, document);
  }));

  return operationGroups.flat().sort(compareOperations);
}

export function getPreferredRequestBody(operation: ApiOperation): RequestBodyOption | undefined {
  const requestBody = operation.requestBody;
  const content = requestBody?.content;
  if (!content) {
    return undefined;
  }

  const contentType = chooseContentType(Object.keys(content));
  if (!contentType) {
    return undefined;
  }

  const mediaType = content[contentType];
  const example = getMediaTypeExample(mediaType);
  const sample = example ?? createSampleFromSchema(mediaType?.schema, operation.document);
  const sampleText = formatBodySample(sample, contentType);

  return {
    contentType,
    required: Boolean(requestBody.required),
    description: requestBody.description ?? '',
    sampleText
  };
}

export function getParameterExample(parameter: OpenApiParameter): string {
  const explicitExample = parameter.example ?? getFirstExampleValue(parameter.examples);
  if (explicitExample !== undefined && explicitExample !== null) {
    return String(explicitExample);
  }

  const schema = parameter.schema;
  if (!schema) {
    return '';
  }

  const schemaExample = schema.example ?? schema.default;
  if (schemaExample !== undefined && schemaExample !== null) {
    return String(schemaExample);
  }

  return '';
}

export function getSchemaTypeLabel(schema: OpenApiSchema | undefined): string {
  if (!schema) {
    return 'string';
  }

  if (schema.format) {
    return `${schema.type ?? 'value'}:${schema.format}`;
  }

  if (schema.type) {
    return schema.type;
  }

  if (schema.$ref) {
    return schema.$ref.split('/').pop() ?? 'object';
  }

  return 'value';
}

export function isAutoGeneratedSummary(summary: string | undefined, path: string, method: string): boolean {
  if (!summary) {
    return true;
  }

  const normalized = summary.trim().toLowerCase();
  return normalized === `${path} - ${method}`.toLowerCase() || normalized === `${method} ${path}`.toLowerCase();
}

function createOperations(api: PublicApiCatalogEntry, document: OpenApiDocument): ApiOperation[] {
  const operations: ApiOperation[] = [];
  const paths = document.paths ?? {};

  for (const [path, pathItem] of Object.entries(paths)) {
    if (!isRecord(pathItem)) {
      continue;
    }

    const pathParameters = Array.isArray(pathItem.parameters) ? pathItem.parameters : [];
    for (const [methodName, rawOperation] of Object.entries(pathItem)) {
      const normalizedMethod = methodName.toLowerCase();
      if (!OPERATION_METHODS.has(normalizedMethod) || !isRecord(rawOperation)) {
        continue;
      }

      const method = normalizedMethod.toUpperCase();
      const operation = rawOperation as OpenApiOperation;
      const parameters = [...pathParameters, ...(operation.parameters ?? [])];
      const summary = isAutoGeneratedSummary(operation.summary, path, method)
        ? createFriendlyOperationName(method, path)
        : operation.summary?.trim() ?? createFriendlyOperationName(method, path);
      const description = normalizeDescription(operation.description ?? '');
      const tags = Array.isArray(operation.tags) ? operation.tags.filter(Boolean) : [];
      const searchText = [
        api.title,
        api.description,
        method,
        path,
        summary,
        description,
        operation.operationId,
        ...tags,
        ...parameters.map((parameter) => parameter.name)
      ].filter(Boolean).join(' ').toLowerCase();

      operations.push({
        key: `${api.id}:${method}:${path}`,
        api,
        apiId: api.id,
        apiTitle: api.title,
        method,
        path,
        summary,
        description,
        operationId: operation.operationId,
        tags,
        parameters,
        requestBody: operation.requestBody,
        responses: operation.responses,
        document,
        searchText
      });
    }
  }

  return operations;
}

function compareOperations(left: ApiOperation, right: ApiOperation): number {
  return left.apiTitle.localeCompare(right.apiTitle)
    || left.path.localeCompare(right.path)
    || HTTP_METHODS.indexOf(left.method as typeof HTTP_METHODS[number]) - HTTP_METHODS.indexOf(right.method as typeof HTTP_METHODS[number]);
}

function chooseContentType(contentTypes: string[]): string | undefined {
  return contentTypes.find((contentType) => contentType.toLowerCase() === 'application/json')
    ?? contentTypes.find((contentType) => contentType.toLowerCase() === 'text/json')
    ?? contentTypes.find((contentType) => contentType.toLowerCase().includes('+json'))
    ?? contentTypes[0];
}

function getMediaTypeExample(mediaType: OpenApiMediaType | undefined): unknown {
  if (!mediaType) {
    return undefined;
  }

  return mediaType.example ?? getFirstExampleValue(mediaType.examples);
}

function getFirstExampleValue(examples: Record<string, { value?: unknown }> | undefined): unknown {
  if (!examples) {
    return undefined;
  }

  const firstExample = Object.values(examples)[0];
  return firstExample?.value;
}

function createSampleFromSchema(schema: OpenApiSchema | undefined, document: OpenApiDocument, seenRefs = new Set<string>()): unknown {
  const resolvedSchema = resolveSchema(schema, document, seenRefs);
  if (!resolvedSchema) {
    return {};
  }

  if (resolvedSchema.example !== undefined) {
    return resolvedSchema.example;
  }

  if (resolvedSchema.default !== undefined) {
    return resolvedSchema.default;
  }

  if (resolvedSchema.enum?.length) {
    return resolvedSchema.enum[0];
  }

  if (resolvedSchema.allOf?.length) {
    return Object.assign({}, ...resolvedSchema.allOf.map((item) => createSampleFromSchema(item, document, seenRefs)));
  }

  if (resolvedSchema.oneOf?.length) {
    return createSampleFromSchema(resolvedSchema.oneOf[0], document, seenRefs);
  }

  if (resolvedSchema.anyOf?.length) {
    return createSampleFromSchema(resolvedSchema.anyOf[0], document, seenRefs);
  }

  if (resolvedSchema.type === 'array') {
    return [createSampleFromSchema(resolvedSchema.items, document, seenRefs)];
  }

  if (resolvedSchema.type === 'object' || resolvedSchema.properties) {
    const sample: Record<string, unknown> = {};
    for (const [propertyName, propertySchema] of Object.entries(resolvedSchema.properties ?? {})) {
      sample[propertyName] = createSampleFromSchema(propertySchema, document, seenRefs);
    }

    return sample;
  }

  if (resolvedSchema.type === 'integer' || resolvedSchema.type === 'number') {
    return 0;
  }

  if (resolvedSchema.type === 'boolean') {
    return true;
  }

  if (resolvedSchema.format === 'date-time') {
    return '2026-07-17T00:00:00Z';
  }

  if (resolvedSchema.format === 'date') {
    return '2026-07-17';
  }

  if (resolvedSchema.format === 'uuid') {
    return '00000000-0000-0000-0000-000000000000';
  }

  return 'string';
}

function resolveSchema(schema: OpenApiSchema | undefined, document: OpenApiDocument, seenRefs: Set<string>): OpenApiSchema | undefined {
  if (!schema?.$ref) {
    return schema;
  }

  const ref = schema.$ref;
  if (seenRefs.has(ref)) {
    return undefined;
  }

  seenRefs.add(ref);
  const schemaName = ref.startsWith('#/components/schemas/') ? ref.slice('#/components/schemas/'.length) : '';
  const referencedSchema = schemaName ? document.components?.schemas?.[schemaName] : undefined;
  return resolveSchema(referencedSchema, document, seenRefs);
}

function formatBodySample(sample: unknown, contentType: string): string {
  if (typeof sample === 'string' && !contentType.toLowerCase().includes('json')) {
    return sample;
  }

  return JSON.stringify(sample ?? {}, null, 2);
}

function createFriendlyOperationName(method: string, path: string): string {
  const segments = path.split('/').filter((segment) => segment && !/^v\d+$/i.test(segment) && segment.toLowerCase() !== 'api');
  const lastResource = segments.filter((segment) => !segment.startsWith('{')).pop() ?? segments[segments.length - 1] ?? 'resource';
  const resourceLabel = toWords(lastResource.replace(/[{}]/g, ''));

  switch (method) {
    case 'GET':
      return path.includes('{') ? `Get ${singularize(resourceLabel)}` : `List ${resourceLabel}`;
    case 'POST':
      return `Create ${singularize(resourceLabel)}`;
    case 'PUT':
      return `Create/Replace ${singularize(resourceLabel)}`;
    case 'PATCH':
      return `Update ${singularize(resourceLabel)}`;
    case 'DELETE':
      return `Delete ${singularize(resourceLabel)}`;
    default:
      return `${method} ${resourceLabel}`;
  }
}

function toWords(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim()
    .toLowerCase();
}

function singularize(value: string): string {
  return value.endsWith('ies') ? `${value.slice(0, -3)}y` : value.replace(/s$/, '');
}

function normalizeDescription(value: string): string {
  return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
